import { useEffect, useState } from "react";
import { Card, CardContent } from "../ui/card";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from "../ui/dialog";
import { Loader2, Wallet, TrendingUp, HandCoins, PiggyBank, Pencil, AlertTriangle, History, Flag, FlagOff, CalendarClock, Trash2, Plus, Info } from "lucide-react";
import { toast } from "sonner";
import { formatCurrency, formatFullCurrency } from "./projectUtils";
import { ConcedidoCobradoChart } from "./ConcedidoCobradoChart";
import { GastoSubvencionPorAnioChart } from "./GastoSubvencionPorAnioChart";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CHART_YEAR_OPTIONS = Array.from({ length: 9 }, (_, i) => String(2022 + i)); // 2022-2030

/**
 * Vista económica de los proyectos I+D importados desde el portafolio PG-07:
 * Previsto vs Incurrido vs Fondos Externos (concedido/devengado) vs Cobrado en caja.
 */
export function EconomicVistaCard({ token, isAdmin }) {
  const [data, setData] = useState(null);
  const [byYearData, setByYearData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(String(new Date().getFullYear()));
  const [editRow, setEditRow] = useState(null); // { id, name, field, currentValue }
  const [editValue, setEditValue] = useState("");
  const [editReason, setEditReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState([]);
  const [flagRow, setFlagRow] = useState(null); // { id, name, nextPending }
  const [flagReason, setFlagReason] = useState("");
  const [flagSaving, setFlagSaving] = useState(false);
  const [reviewRow, setReviewRow] = useState(null); // { id, name, review_due }
  const [newReviewYear, setNewReviewYear] = useState("");
  const [newReviewDate, setNewReviewDate] = useState("");
  const [newReviewReason, setNewReviewReason] = useState("");
  const [reviewSaving, setReviewSaving] = useState(false);
  const yearStr = selectedYear;

  const load = () => {
    if (!token) return;
    setLoading(true);
    fetch(`${API}/pg07/economic-summary?year=${yearStr}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => j && setData(j))
      .catch(() => {})
      .finally(() => setLoading(false));
    fetch(`${API}/pg07/economic-summary/by-year`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => j && setByYearData(j.rows))
      .catch(() => {});
  };

  useEffect(() => { load(); }, [token, yearStr]);

  const openEdit = (row, field) => {
    const currentValue = field === "external_funds_by_year" ? row.external_year : row.collected_year;
    setEditRow({ id: row.id, name: row.name, field });
    setEditValue(String(currentValue ?? 0));
    setEditReason("");
  };

  const saveEdit = async () => {
    if (!editRow) return;
    const value = parseFloat(editValue);
    if (Number.isNaN(value)) { toast.error("Importe inválido"); return; }
    setSaving(true);
    try {
      const r = await fetch(`${API}/pg07/projects/${editRow.id}/economic-field`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ field: editRow.field, year: yearStr, value, reason: editReason || undefined }),
      });
      if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.detail || "Error al guardar"); }
      toast.success("Valor actualizado y registrado en el historial");
      setEditRow(null);
      load();
    } catch (e) {
      toast.error(e.message || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const openHistory = async () => {
    setHistoryOpen(true);
    try {
      const r = await fetch(`${API}/pg07/economic-summary/edit-history`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r.ok) setHistory(await r.json());
    } catch { /* silent */ }
  };

  const openFlagToggle = (row) => {
    setFlagRow({ id: row.id, name: row.name, nextPending: !row.pending_concedido_verification });
    setFlagReason("");
  };

  const submitFlagToggle = async () => {
    if (!flagRow) return;
    setFlagSaving(true);
    try {
      const r = await fetch(`${API}/pg07/projects/${flagRow.id}/economic-pending-flag`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ year: yearStr, pending: flagRow.nextPending, reason: flagReason || undefined }),
      });
      if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.detail || "Error al guardar"); }
      toast.success(flagRow.nextPending ? "Marcado como pendiente de verificación" : "Marcado como verificado");
      setFlagRow(null);
      load();
    } catch (e) {
      toast.error(e.message || "Error al guardar");
    } finally {
      setFlagSaving(false);
    }
  };

  const openReviewDue = (row) => {
    setReviewRow({ id: row.id, name: row.name, review_due: row.review_due || {} });
    setNewReviewYear(String(Number(yearStr) + 1));
    setNewReviewDate("");
    setNewReviewReason("");
  };

  const addReviewDue = async () => {
    if (!reviewRow || !newReviewYear || !newReviewDate) { toast.error("Indica año y fecha"); return; }
    setReviewSaving(true);
    try {
      const r = await fetch(`${API}/pg07/projects/${reviewRow.id}/economic-review-due`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ year: newReviewYear, due_date: newReviewDate, reason: newReviewReason || undefined }),
      });
      if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.detail || "Error al guardar"); }
      setReviewRow((prev) => ({ ...prev, review_due: { ...prev.review_due, [newReviewYear]: newReviewDate } }));
      setNewReviewYear(String(Number(newReviewYear) + 1));
      setNewReviewDate("");
      setNewReviewReason("");
      toast.success("Fecha de revisión guardada");
      load();
    } catch (e) {
      toast.error(e.message || "Error al guardar");
    } finally {
      setReviewSaving(false);
    }
  };

  const deleteReviewDue = async (yr) => {
    if (!reviewRow) return;
    try {
      const r = await fetch(`${API}/pg07/projects/${reviewRow.id}/economic-review-due/${yr}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error("Error al eliminar");
      setReviewRow((prev) => {
        const next = { ...prev.review_due };
        delete next[yr];
        return { ...prev, review_due: next };
      });
      toast.success("Fecha eliminada");
      load();
    } catch (e) {
      toast.error(e.message || "Error al eliminar");
    }
  };

  if (loading) return <Card><CardContent className="p-8 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-emerald-600" /></CardContent></Card>;
  if (!data || !data.rows?.length) return null;

  const t = data.totals;
  const remaining = t.previsto - t.incurrido_all;
  const filteredRows = data.rows.filter(r => r.planned > 0 || r.incurred_all > 0 || r.external_all > 0 || r.collected_all > 0);
  const top20 = filteredRows.slice(0, 20);
  const pendingOutsideTop20 = filteredRows.slice(20).filter(r => r.pending_concedido_verification);
  const rows = [...top20, ...pendingOutsideTop20];

  return (
    <Card data-testid="economic-vista-card">
      <CardContent className="p-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <div className="text-xs uppercase tracking-widest text-emerald-700 font-semibold mb-1">Vista económica portafolio</div>
            <h3 className="text-lg font-bold flex items-center gap-2">
              <Wallet className="w-4 h-4" /> Previsto vs Incurrido vs Fondos Externos · {data.year}
            </h3>
          </div>
          <div className="flex items-center gap-2">
            <Select value={selectedYear} onValueChange={setSelectedYear}>
              <SelectTrigger className="w-[110px] h-9" data-testid="economic-year-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CHART_YEAR_OPTIONS.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={openHistory} data-testid="economic-edit-history-btn">
              <History className="w-3.5 h-3.5 mr-1.5" />Historial de ediciones
            </Button>
          </div>
        </div>

        {t.pending_projects_count > 0 && (
          <div className="flex items-center gap-2 mt-3 rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-900 px-3 py-2 text-xs text-amber-800 dark:text-amber-300" data-testid="economic-pending-banner">
            <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
            <span>{t.pending_projects_count} proyecto(s) tienen la subvención "concedida {data.year}" igualada provisionalmente al cobro de Tesorería, pendiente de verificar con la resolución oficial. Editable abajo (icono lápiz) o marcable/desmarcable con el icono de bandera.</span>
          </div>
        )}

        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mt-4" data-testid="economic-kpis">
          <KpiBig icon={<Wallet className="w-4 h-4" />} label="Previsto total" value={t.previsto} color="slate"
            infoTip="Presupuesto consolidado en PG-07 (aceptado en convocatoria/consorcio). Puede diferir del campo 'Presupuesto' de la ficha individual del proyecto, más arriba en esta página." />
          <KpiBig icon={<TrendingUp className="w-4 h-4" />} label={`Incurrido ${data.year}`} value={t.incurrido_year} color="red" sub={`Total: ${formatCurrency(t.incurrido_all)}`} />
          <KpiBig icon={<HandCoins className="w-4 h-4" />} label={`Concedido ${data.year}`} value={t.externos_year} color="amber" sub={`Total: ${formatCurrency(t.externos_all)}`} />
          <KpiBig icon={<PiggyBank className="w-4 h-4" />} label={`Cobrado en caja ${data.year}`} value={t.collected_year} color="teal" sub={`Total: ${formatCurrency(t.collected_all)}`} />
          <KpiBig icon={<Wallet className="w-4 h-4" />} label="Pendiente ejecutar" value={remaining} color={remaining >= 0 ? "emerald" : "red"} />
          <KpiBig icon={<HandCoins className="w-4 h-4" />} label="% Cobertura ext." value={t.coverage_pct_all != null ? `${t.coverage_pct_all}%` : "—"} color="violet" text />
        </div>

        <div className="mt-5">
          <ConcedidoCobradoChart rows={data.rows} year={data.year} />
        </div>

        <div className="mt-6 border-t pt-5">
          <GastoSubvencionPorAnioChart rows={byYearData} currentYear={yearStr} />
        </div>

        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-xs" data-testid="economic-table">
            <thead>
              <tr className="border-b bg-muted/30">
                <th className="text-left px-2 py-2 font-medium">Proyecto</th>
                <th className="text-left px-2 py-2 font-medium">Fase</th>
                <th className="text-right px-2 py-2 font-medium">Previsto</th>
                <th className="text-right px-2 py-2 font-medium">Incurrido total</th>
                <th className="text-right px-2 py-2 font-medium">Concedido {data.year}</th>
                <th className="text-right px-2 py-2 font-medium">Cobrado en caja {data.year}</th>
                <th className="text-right px-2 py-2 font-medium">% Cobertura</th>
                <th className="text-right px-2 py-2 font-medium">Pendiente</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.id} className="border-b" data-testid={`economic-row-${i}`}>
                  <td className="px-2 py-1.5 max-w-[260px] truncate" title={r.name}>{r.name}</td>
                  <td className="px-2 py-1.5"><Badge variant="secondary" className="text-[9px]">{r.phase}</Badge></td>
                  <td className="px-2 py-1.5 text-right font-mono">
                    {formatFullCurrency(r.planned)}
                    {r.consortium_total > 0 && (
                      <div className="text-[9px] text-muted-foreground" data-testid={`consortium-total-hint-${i}`} title="Presupuesto total del consorcio (todos los socios); Previsto muestra solo la parte de Valoriza">
                        de consorcio: {formatFullCurrency(r.consortium_total)}
                      </div>
                    )}
                  </td>
                  <td className="px-2 py-1.5 text-right font-mono text-red-700">{formatFullCurrency(r.incurred_all)}</td>
                  <td className="px-2 py-1.5 text-right font-mono text-amber-700">
                    <div className="flex items-center justify-end gap-1">
                      {r.pending_concedido_verification && (
                        <Badge variant="outline" className="text-[8px] border-amber-400 text-amber-700" data-testid={`pending-badge-${i}`}>pendiente</Badge>
                      )}
                      {formatFullCurrency(r.external_year)}
                      {isAdmin && (
                        <button
                          onClick={() => openEdit(r, "external_funds_by_year")}
                          className="text-muted-foreground hover:text-emerald-600"
                          data-testid={`edit-concedido-${i}`}
                          title="Editar valor concedido"
                        >
                          <Pencil className="w-3 h-3" />
                        </button>
                      )}
                      {isAdmin && (
                        <button
                          onClick={() => openFlagToggle(r)}
                          className="text-muted-foreground hover:text-amber-600"
                          data-testid={`flag-toggle-${i}`}
                          title={r.pending_concedido_verification ? "Marcar como verificado" : "Marcar como pendiente de verificación"}
                        >
                          {r.pending_concedido_verification ? <FlagOff className="w-3 h-3" /> : <Flag className="w-3 h-3" />}
                        </button>
                      )}
                      {isAdmin && (
                        <button
                          onClick={() => openReviewDue(r)}
                          className={`hover:text-sky-600 ${Object.keys(r.review_due || {}).length ? "text-sky-600" : "text-muted-foreground"}`}
                          data-testid={`review-due-btn-${i}`}
                          title="Fechas de revisión pendientes (resolución oficial futura)"
                        >
                          <CalendarClock className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                    {Object.keys(r.review_due || {}).length > 0 && (
                      <div className="text-[9px] text-sky-700 mt-0.5" data-testid={`review-due-hint-${i}`}>
                        Revisar: {Object.entries(r.review_due).map(([y, d]) => `${y} (${d})`).join(", ")}
                      </div>
                    )}
                  </td>
                  <td className="px-2 py-1.5 text-right font-mono text-teal-700">
                    <div className="flex items-center justify-end gap-1">
                      {formatFullCurrency(r.collected_year)}
                      {isAdmin && (
                        <button
                          onClick={() => openEdit(r, "external_funds_collected_by_year")}
                          className="text-muted-foreground hover:text-emerald-600"
                          data-testid={`edit-cobrado-${i}`}
                          title="Editar valor cobrado"
                        >
                          <Pencil className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                  </td>
                  <td className="px-2 py-1.5 text-right font-mono">
                    <div className="flex items-center justify-end gap-1">
                      {r.coverage_pct != null ? `${r.coverage_pct}%` : "—"}
                      {r.coverage_pct != null && r.coverage_pct > 200 && (
                        <span title="Cobertura acumulada incluye una concesión histórica de importe elevado en un año concreto (verificado con la fuente oficial)" data-testid={`coverage-warn-${i}`}>
                          <Info className="w-3 h-3 text-blue-500" />
                        </span>
                      )}
                    </div>
                  </td>
                  <td className={`px-2 py-1.5 text-right font-mono ${r.remaining != null && r.remaining < 0 ? "text-red-700" : "text-emerald-700"}`}>
                    <div className="flex items-center justify-end gap-1">
                      {r.remaining != null ? formatFullCurrency(r.remaining) : "—"}
                      {r.remaining != null && r.remaining <= -1 && (
                        <span title="Incurrido acumulado ligeramente por encima del presupuesto previsto (revisar si el previsto necesita actualizarse en la fuente)" data-testid={`remaining-warn-${i}`}>
                          <Info className="w-3 h-3 text-red-500" />
                        </span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {data.rows.length > 20 && (
            <div className="text-[11px] text-muted-foreground mt-2">
              Mostrando {rows.length} proyectos ({top20.length} con más presupuesto previsto{pendingOutsideTop20.length > 0 ? ` + ${pendingOutsideTop20.length} pendiente(s) de verificar fuera del top 20` : ""}, de {data.rows.length} totales).
            </div>
          )}
        </div>
      </CardContent>

      <Dialog open={!!editRow} onOpenChange={(o) => !o && setEditRow(null)}>
        <DialogContent data-testid="economic-edit-dialog">
          <DialogHeader>
            <DialogTitle>
              Editar {editRow?.field === "external_funds_by_year" ? "concedido/devengado" : "cobrado en caja"} {yearStr}
            </DialogTitle>
            <DialogDescription>{editRow?.name} — el cambio queda registrado en el historial de ediciones con tu usuario y fecha.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Importe (€)</Label>
              <Input type="number" step="0.01" value={editValue} onChange={(e) => setEditValue(e.target.value)} data-testid="economic-edit-value-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Motivo / fuente (opcional)</Label>
              <Textarea value={editReason} onChange={(e) => setEditReason(e.target.value)} placeholder="Ej: Resolución oficial concedido 2026, folio 5" data-testid="economic-edit-reason-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditRow(null)}>Cancelar</Button>
            <Button onClick={saveEdit} disabled={saving} data-testid="economic-edit-save-btn">
              {saving && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />}Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="max-w-lg" data-testid="economic-history-dialog">
          <DialogHeader>
            <DialogTitle>Historial de ediciones económicas</DialogTitle>
            <DialogDescription>Cambios manuales sobre concedido/cobrado, con trazabilidad de autor y fecha.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-[420px] overflow-y-auto">
            {!history.length ? (
              <p className="text-sm text-muted-foreground">Sin ediciones manuales registradas todavía.</p>
            ) : history.map((h) => (
              <div key={h.id} className="p-2.5 rounded-lg border text-xs" data-testid={`economic-history-item-${h.id}`}>
                <div className="flex items-center gap-1.5 flex-wrap mb-1">
                  <Badge variant="secondary">{h.project_name}</Badge>
                  <span className="text-muted-foreground">
                    {h.field === "external_funds_by_year" ? "concedido" : h.field === "external_funds_collected_by_year" ? "cobrado" : h.field === "external_funds_review_due" ? "fecha de revisión" : h.field === "budget_planned" ? "previsto" : "marca de pendiente"} {h.year}
                  </span>
                </div>
                {typeof h.new_value === "number" ? (
                  <p>{formatFullCurrency(h.old_value)} → <span className="font-semibold">{formatFullCurrency(h.new_value)}</span></p>
                ) : (
                  <p>Estado → <span className="font-semibold capitalize">{h.new_value}</span></p>
                )}
                {h.reason && <p className="text-muted-foreground mt-0.5">{h.reason}</p>}
                <p className="text-muted-foreground mt-0.5">{h.edited_by} · {new Date(h.edited_at).toLocaleString("es-ES")}</p>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!flagRow} onOpenChange={(o) => !o && setFlagRow(null)}>
        <DialogContent data-testid="economic-flag-dialog">
          <DialogHeader>
            <DialogTitle>
              {flagRow?.nextPending ? "Marcar concedido como pendiente de verificación" : "Marcar concedido como verificado"}
            </DialogTitle>
            <DialogDescription>{flagRow?.name} — {yearStr}. Esto no cambia el importe, solo el estado de verificación. Queda registrado en el historial.</DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label>Motivo (opcional)</Label>
            <Textarea value={flagReason} onChange={(e) => setFlagReason(e.target.value)} placeholder="Ej: se detectó error tras validar, revisar de nuevo con Finanzas" data-testid="economic-flag-reason-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFlagRow(null)}>Cancelar</Button>
            <Button onClick={submitFlagToggle} disabled={flagSaving} data-testid="economic-flag-confirm-btn">
              {flagSaving && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />}Confirmar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!reviewRow} onOpenChange={(o) => !o && setReviewRow(null)}>
        <DialogContent data-testid="economic-review-due-dialog">
          <DialogHeader>
            <DialogTitle>Fechas de revisión pendientes</DialogTitle>
            <DialogDescription>{reviewRow?.name} — avisa por email al equipo cuando llegue la fecha esperada de resolución oficial de un año aún sin concedido verificado.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-[220px] overflow-y-auto">
            {!Object.keys(reviewRow?.review_due || {}).length ? (
              <p className="text-sm text-muted-foreground">Sin fechas registradas.</p>
            ) : Object.entries(reviewRow.review_due).sort().map(([yr, d]) => (
              <div key={yr} className="flex items-center justify-between p-2 rounded-lg border text-sm" data-testid={`review-due-item-${yr}`}>
                <span>Año <b>{yr}</b> — prevista {d}</span>
                <button onClick={() => deleteReviewDue(yr)} className="text-red-500 hover:text-red-700" data-testid={`review-due-delete-${yr}`} title="Eliminar">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
          <div className="border-t pt-3 space-y-2">
            <Label className="text-xs">Añadir nueva fecha</Label>
            <div className="grid grid-cols-2 gap-2">
              <Input placeholder="Año (ej. 2027)" value={newReviewYear} onChange={(e) => setNewReviewYear(e.target.value)} data-testid="review-due-year-input" />
              <Input type="date" value={newReviewDate} onChange={(e) => setNewReviewDate(e.target.value)} data-testid="review-due-date-input" />
            </div>
            <Textarea placeholder="Motivo / fuente (opcional)" value={newReviewReason} onChange={(e) => setNewReviewReason(e.target.value)} data-testid="review-due-reason-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReviewRow(null)}>Cerrar</Button>
            <Button onClick={addReviewDue} disabled={reviewSaving} data-testid="review-due-add-btn">
              {reviewSaving ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Plus className="w-3.5 h-3.5 mr-1.5" />}Añadir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

// Tailwind JIT safelist (clases dinámicas construidas por color, deben existir literalmente):
// bg-teal-50/60 dark:bg-teal-950/20 text-teal-700 dark:text-teal-300
function KpiBig({ icon, label, value, color = "slate", sub, text = false, infoTip }) {
  return (
    <div className={`rounded-lg border p-3 bg-${color}-50/60 dark:bg-${color}-950/20`}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-muted-foreground">
        {icon}{label}
        {infoTip && (
          <span title={infoTip} className="cursor-help" data-testid="previsto-info-tip">
            <Info className="w-3 h-3 opacity-60" />
          </span>
        )}
      </div>
      <div className={`text-xl font-black text-${color}-700 dark:text-${color}-300 mt-1`}>
        {text ? value : formatCurrency(value)}
      </div>
      {sub && <div className="text-[10px] text-muted-foreground mt-0.5">{sub}</div>}
    </div>
  );
}
